import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { participantsApi, MagicLinkSessionStatus } from '@/api/endpoints/participants';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { useDebounce } from '@/hooks/useDebounce';

const STATUS_LABELS: Record<MagicLinkSessionStatus, string> = {
  NOT_STARTED: 'Nie rozpoczął',
  IN_PROGRESS: 'W trakcie',
  COMPLETED: 'Zakończony',
};

const STATUS_CLASSES: Record<MagicLinkSessionStatus, string> = {
  NOT_STARTED: 'bg-gray-100 text-gray-600',
  IN_PROGRESS: 'bg-yellow-100 text-yellow-700',
  COMPLETED: 'bg-green-100 text-green-700',
};

function StatusBadge({ status }: { status: MagicLinkSessionStatus | null }) {
  if (!status) {
    return <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-400">Brak sesji</span>;
  }
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASSES[status]}`}>
      {STATUS_LABELS[status]}
    </span>
  );
}

export const SessionStatusPage = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const { data, isLoading } = useQuery({
    queryKey: ['session-status', { page, search: debouncedSearch }],
    queryFn: () =>
      participantsApi.getMagicLinkStatus({
        page,
        limit: 20,
        search: debouncedSearch || undefined,
        stage: 2,
        term: 1,
      }),
    refetchInterval: 30_000,
  });

  const students = data?.data ?? [];
  const meta = data?.meta;
  const stats = data?.stats;

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Status uczestników – Etap 2</h1>
        <p className="mt-1 text-sm text-gray-500">
          Lista uczestników finału wraz ze statusem testu. Odświeżanie co 30 sekund.
        </p>
      </div>

      {stats && (
        <div className="flex gap-3">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-gray-800">{stats.totalStudents}</p>
              <p className="text-xs text-gray-500">Wszyscy uczestnicy</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Uczestnicy Etap 2</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <Input
              placeholder="Szukaj po imieniu, nazwisku lub emailu..."
              value={search}
              onChange={handleSearchChange}
              className="max-w-sm"
            />
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Lp.</TableHead>
                    <TableHead>Imię i nazwisko</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Szkoła</TableHead>
                    <TableHead>Status testu</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {students.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="py-8 text-center text-gray-400">
                        Brak uczestników
                      </TableCell>
                    </TableRow>
                  ) : (
                    students.map((student, index) => (
                      <TableRow key={student.id}>
                        <TableCell className="text-gray-400">
                          {((meta?.page ?? 1) - 1) * (meta?.limit ?? 20) + index + 1}
                        </TableCell>
                        <TableCell className="font-medium">
                          {student.firstName} {student.lastName}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">{student.email}</TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {student.school?.name ?? <span className="text-gray-400">—</span>}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={student.sessionStatus} />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>

              {meta && meta.totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                  <span>
                    Strona {meta.page} z {meta.totalPages} ({meta.total} uczestników)
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={meta.page <= 1}
                      className="rounded px-3 py-1 hover:bg-gray-100 disabled:opacity-40"
                    >
                      ← Poprzednia
                    </button>
                    <button
                      onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
                      disabled={meta.page >= meta.totalPages}
                      className="rounded px-3 py-1 hover:bg-gray-100 disabled:opacity-40"
                    >
                      Następna →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
